import React, { useState, useEffect } from 'react';
import { createPortal } from "react-dom";
import ReactDOM from 'react-dom';
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import PdfHighlightViewer from './PdfHighlightViewer';
import DocxHighlightViewer from './Docxhighlightviewer';
import ErrorPopup from './popup/ErrorPopup';
import axios from 'axios';
import GetCookie from './GetCookie';

const DocumentPreview = ({ 
  isOpen, 
  onClose, 
  docId, 
  documentName, 
  s3_path = null, 
  bucket_type = "input",
  presigned_url = null,
  showOpenInTab = false,
  highlightText = null,
  highlightChunkContent = null,
  highlightTableCaption = null
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentInfo, setDocumentInfo] = useState(null);
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Determine file type from document name
  const getFileType = (fileName) => {
    if (!fileName) return 'pdf'; // Default
    
    const extension = fileName.split('.').pop().toLowerCase();
    // Map common extensions to file types DocViewer can handle
    const extensionMap = {
      'pdf': 'pdf',
      'doc': 'doc',
      'docx': 'docx',
      'ppt': 'ppt',
      'pptx': 'pptx',
      'xls': 'xls',
      'xlsx': 'xlsx',
      'xlsm': 'xlsx',  //Excel with macros
      'xlsb': 'xlsx',  // Excel binary format
      'xltx': 'xlsx',  // Excel template
      'xltm': 'xlsx',  // Excel template with macros
      'txt': 'txt',
      'png': 'png',
      'jpg': 'jpg',
      'jpeg': 'jpeg',
      'csv': 'csv',
      'py': 'txt',
      'json': 'txt',
      'r': 'txt'
    };
    
    return extensionMap[extension] || 'pdf';
  };

  const fetchDocumentUrl = async () => {
    setLoading(true);
    
    try {
      let response;
      let fileName = documentName;
      
      // Determine which API to call based on bucket_type and available props
      if (bucket_type === "output" && s3_path) {
        // Use output document preview API
        response = await axios.post('/pceapi/threads/unstructured/output-document-preview', {
          s3_path: s3_path
        }, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `${GetCookie('access_token')}`,
          }
        });
        
        // Extract filename from s3_path if documentName not provided
        if (!fileName && s3_path) {
          fileName = s3_path.split('/').pop();
        }
      } else if (docId) {
        // Use input document preview API (existing functionality)
        response = await axios.post('/pceapi/threads/unstructured/document-preview', {
          doc_id: docId
        }, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `${GetCookie('access_token')}`,
          }
        });
        
      } else {
        // Invalid configuration
        setDocumentInfo(null);
        setErrorMessage('Invalid configuration: Either provide docId for input bucket or s3_path with bucket_type="output"');
        setIsErrorPopupVisible(true);
        return;
      }
      
      if (response.data.status === 'success') {
        console.log(response)
        let fileType = 'pdf'
  
        if (response.data.extension === null || response.data.extension === undefined){
      
        fileType = getFileType(documentName);
      }
        else {
            fileType = 'pdf'
          
        }
        
        setDocumentInfo([{
          uri: response.data.preview_url,
          fileType: fileType,
          fileName: fileName || response.data.filename || 'document'
        }]);
        
        setError(null);
      } else {
        setDocumentInfo(null);
        setErrorMessage(response.data.message || 'Failed to load document preview');
        setIsErrorPopupVisible(true);
      }
    } catch (error) {
      setDocumentInfo(null);
      setErrorMessage(error.message || 'Error generating document preview');
      setIsErrorPopupVisible(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      // If a pre-signed URL is provided directly, skip the API call
      if (presigned_url) {
        const fileType = getFileType(documentName);
        setDocumentInfo([{
          uri: presigned_url,
          fileType: fileType,
          fileName: documentName || 'document'
        }]);
        setError(null);
        setLoading(false);
        return;
      }

      // Validate required props based on bucket_type
      if (bucket_type === "output" && !s3_path) {
        setErrorMessage('s3_path is required when bucket_type is "output"');
        setIsErrorPopupVisible(true);
        setLoading(false);
        return;
      }
      
      if (bucket_type === "input" && !docId) {
        setErrorMessage('docId is required when bucket_type is "input"');
        setIsErrorPopupVisible(true);
        setLoading(false);
        return;
      }
      
      fetchDocumentUrl();
    }
  }, [isOpen, docId, s3_path, bucket_type, presigned_url]);

  // Close error popup
  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };

  // Determine display name for modal header
  const getRawDisplayName = () => {
    if (documentName) return documentName;
    if (s3_path) return s3_path.split('/').pop();
    return 'Document Preview';
  };

  const getDisplayName = () => {
    const name = getRawDisplayName();
    return name.length > 50 ? name.substring(0, 50) + '...' : name;
  };

 // --- FIX: MEMOIZE CONFIG TO PREVENT THUMBNAIL CRASH ---
  const memoizedConfig = React.useMemo(() => ({
    header: {
      disableHeader: true,
      disableFileName: true,
      retainURLParams: true // MUST BE TRUE FOR AWS S3 URLS
    },
    pdfVerticalScrollByDefault: true
  }), []);

  const hasHighlightData = highlightTableCaption || highlightText || highlightChunkContent;

  if (!isOpen) return null;

  return createPortal(
    <div className="modal d-block bg-dark bg-opacity-50" style={{ zIndex: "var(--num-1000000000)" }} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
      <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
        <div className="modal-content ev-card-3">
          <div className="row modal-header border-0">
              <div className="col-10">
              <h6 className="ev-message-card3 m-0 avatar-tooltip" data-tooltip={getRawDisplayName()}>{getDisplayName()}</h6>
              </div>
              <div className="col-2 text-end d-flex align-items-center justify-content-end gap-2">
                {showOpenInTab && documentInfo?.[0]?.uri && (
                  <button
                    type="button"
                    className="btn ev-btn-lightblue-fill-round ev-radius-xsmall ev-btn-med avatar-tooltip"
                    data-tooltip="Open in New Tab"
                    onClick={() => window.open(documentInfo[0].uri, "_blank", "noopener,noreferrer")}
                  >
                    <i className="bi bi-box-arrow-up-right"></i>
                  </button>
                )}
                <button type="button" className="ev-btn-text-only-fill ev-fs-20" onClick={onClose}><i className="bi bi-x"></i></button>
              </div>
          </div>
          <div className="modal-body p-0 ev-radius-small vh-80">
            {loading ? (
              <div className="text-center p-5">
                <div className="spinner-border ev-med-p-blue" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : documentInfo ? (
              hasHighlightData && documentInfo[0]?.fileType === 'pdf' ? (
                <PdfHighlightViewer
                  pdfUrl={documentInfo[0].uri}
                  text1={highlightTableCaption}
                  text2={highlightText}
                  text3={highlightChunkContent}
                />
              ) : hasHighlightData && documentInfo[0]?.fileType === 'docx' ? (
                <DocxHighlightViewer
                  docUrl={documentInfo[0].uri}
                  text1={highlightTableCaption}
                  text2={highlightText}
                  text3={highlightChunkContent}
                  fallback={
                    <DocViewer 
                      documents={documentInfo}
                      className="react-doc-viewer h-100"
                      pluginRenderers={DocViewerRenderers}
                      config={memoizedConfig} 
                    />
                  }
                />
              ) : (
                <DocViewer 
                  documents={documentInfo}
                  className="react-doc-viewer h-100"
                  pluginRenderers={DocViewerRenderers}
                  config={memoizedConfig} 
                />
              )
            ) : (
              <div className="text-center p-5">
                <p>Unable to load document preview.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Error Popup */}
      {isErrorPopupVisible && (
        <ErrorPopup 
          onClose={closeErrorPopup}
          message={errorMessage}
        />
      )}
    </div>,
    document.body
  );
};

export default DocumentPreview;
